import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, MapPin } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PractitionerCard } from "@/components/PractitionerCard";
import { SEO } from "@/components/SEO";

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const [homeCountry, setHomeCountry] = useState("");

  const categoryId = params.get("category") || "";
  const subcategoryId = params.get("subcategory") || "";
  const country = params.get("country") || "";
  const city = params.get("city") || "";
  const activeCategory = categories.find((c) => c.id === categoryId);
  const cityOptions = country
    ? (locations.find((l) => l.country === country)?.cities || [])
    : locations.flatMap((l) => l.cities);

  const nearMeActive = !country && homeCountry &&
    items.some((p) => (p.country || "").toLowerCase() === homeCountry.toLowerCase()) &&
    items.some((p) => (p.country || "").toLowerCase() !== homeCountry.toLowerCase());
  const displayItems = !country && homeCountry
    ? [...items].sort((a, b) =>
        ((b.country || "").toLowerCase() === homeCountry.toLowerCase()) -
        ((a.country || "").toLowerCase() === homeCountry.toLowerCase()))
    : items;

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/practitioners/locations").then((r) => setLocations(r.data));
    api.get("/geo/detect").then((r) => setHomeCountry(r.data.country || "")).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (categoryId) q.category_id = categoryId;
    if (subcategoryId) q.subcategory_id = subcategoryId;
    if (params.get("search")) q.search = params.get("search");
    if (country) q.country = country;
    if (city) q.city = city;
    api.get("/practitioners", { params: q }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [params, categoryId, subcategoryId, country, city]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key === "category") next.delete("subcategory");
    if (key === "country") next.delete("city");
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <SEO title="Find a Practitioner" description="Browse vetted spiritual, wellness, counselling, and naturopathy practitioners worldwide. Every profile personally approved." />
      <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-2">Find your practitioner</h1>
      <p className="text-muted-foreground text-sm mb-10">Every profile here is personally vetted. Wherever you are in the world.</p>

      <div className="flex flex-col sm:flex-row gap-3 max-w-3xl mb-8">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="browse-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setParam("search", search)}
            placeholder="Search practitioners, services…" className="pl-10 rounded-full bg-card/70" />
        </div>
        <Select value={country || "all"} onValueChange={(v) => setParam("country", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="browse-country-select" className="sm:w-44 rounded-full bg-card/70">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All countries</SelectItem>
            {locations.map((l) => <SelectItem key={l.country} value={l.country}>{l.country}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={city || "all"} onValueChange={(v) => setParam("city", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="browse-city-select" className="sm:w-40 rounded-full bg-card/70">
            <SelectValue placeholder="City" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All cities</SelectItem>
            {[...new Set(cityOptions)].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="browse-category-filters">
        <Badge onClick={() => setParam("category", "")} variant={!categoryId ? "default" : "secondary"}
          className="cursor-pointer px-4 py-1.5 rounded-full font-normal">All</Badge>
        {categories.map((c) => (
          <Badge key={c.id} data-testid={`browse-category-${c.slug}`} onClick={() => setParam("category", c.id)}
            variant={categoryId === c.id ? "default" : "secondary"}
            className="cursor-pointer px-4 py-1.5 rounded-full font-normal">{c.name}</Badge>
        ))}
      </div>

      {activeCategory?.subcategories?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8 pl-1" data-testid="browse-subcategory-filters">
          {activeCategory.subcategories.map((s) => (
            <button key={s.id} onClick={() => setParam("subcategory", subcategoryId === s.id ? "" : s.id)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${subcategoryId === s.id ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {s.name}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground py-20 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <div className="py-20 text-center" data-testid="browse-empty-state">
          <p className="font-display text-2xl text-foreground mb-2">No practitioners here yet</p>
          <p className="text-sm text-muted-foreground">This corner of the community is still growing. Check back soon.</p>
        </div>
      ) : (
        <div data-testid="browse-results">
          {nearMeActive && (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4" data-testid="near-me-hint">
              <MapPin size={11} className="text-primary" /> Showing practitioners in {homeCountry} first
            </p>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayItems.map((p) => <PractitionerCard key={p.id} p={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
